import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Button } from '@/components/common/Button'
import { timetableImportV2Api } from '@/services/api/timetableImportV2'
import { PdfTeacherPreviewModal } from './PdfTeacherPreviewModal'
import { Loader2, CheckCircle2, SkipForward, Eye, X, Search } from 'lucide-react'

interface SessionInfo {
  session_id: string
  section_pages: number[]
  section_identifier: string | null
}

interface TeacherAssignmentDialogProps {
  batchId: string
  pendingTeachers: string[]
  perTeacherCounts: Record<string, number>
  perTeacherSessions?: Record<string, SessionInfo | SessionInfo[]>
  onAssigned: (teacherCode: string) => void
  onSkipped: (teacherCode: string) => void
  onClose: () => void
  onComplete: () => void
  onSkipAllComplete?: () => void
}

interface UserSuggestion {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  confidence: number
  teacher_code?: string | null
}

export function TeacherAssignmentDialog({
  batchId,
  pendingTeachers,
  perTeacherCounts,
  perTeacherSessions,
  onAssigned,
  onSkipped,
  onComplete,
  onSkipAllComplete,
  onClose,
}: TeacherAssignmentDialogProps) {
  const [selectedTeacher, setSelectedTeacher] = useState<string>(pendingTeachers[0] || '')
  const [userSuggestions, setUserSuggestions] = useState<Record<string, UserSuggestion[]>>({})
  const [selectedUser, setSelectedUser] = useState<Record<string, string | null>>({})
  const [assigning, setAssigning] = useState<string | null>(null)
  const [createUserMode, setCreateUserMode] = useState<Record<string, boolean>>({})
  const [newUserEmail, setNewUserEmail] = useState<Record<string, string>>({})
  const [loadingSuggestions, setLoadingSuggestions] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [activePdfSessionIndex, setActivePdfSessionIndex] = useState(0)

  const pendingTeachersRef = useRef(pendingTeachers)
  pendingTeachersRef.current = pendingTeachers
  const hadTeachersRef = useRef(pendingTeachers.length > 0)

  // Helper function to format teacher code for display
  const formatTeacherCode = useCallback((code: string): string => {
    if (code.startsWith('__UNKNOWN__')) {
      const suffix = code.replace('__UNKNOWN__', '')
      if (suffix.startsWith('page')) {
        return `Unbekannt (Seite ${suffix.replace('page', '')})`
      } else if (suffix.startsWith('session_')) {
        return `Unbekannt (${suffix.replace('session_', '')}...)`
      } else if (suffix) {
        return `Unbekannt (${suffix})`
      }
      return 'Unbekannt'
    }
    return code
  }, [])

  const getSessionsForTeacher = useCallback((code: string): SessionInfo[] => {
    const sessionData = perTeacherSessions?.[code]
    if (!sessionData) return []
    if (Array.isArray(sessionData)) return sessionData
    return [sessionData]
  }, [perTeacherSessions])

  // For PDF preview, take only the first session if there are multiple
  const sessions = getSessionsForTeacher(selectedTeacher).slice(0, 1)

  useEffect(() => {
    if (pendingTeachers.length > 0) hadTeachersRef.current = true
    if (hadTeachersRef.current && pendingTeachers.length === 0 && !assigning) {
      onComplete()
    }
  }, [pendingTeachers.length, onComplete, assigning])

  useEffect(() => {
    const next = pendingTeachers[0]
    if (!next) return
    if (!pendingTeachers.includes(selectedTeacher)) {
      setSelectedTeacher(next)
    }
  }, [pendingTeachers, selectedTeacher])

  useEffect(() => {
    // Load fresh suggestions for all pending teachers whenever the list changes.
    // Always reload - don't cache, because the available users change after each assignment.
    let cancelled = false
    
    const loadAll = async () => {
      for (const code of pendingTeachers) {
        if (cancelled) return
        setLoadingSuggestions(prev => ({ ...prev, [code]: true }))
        try {
          const data = await timetableImportV2Api.getUserSuggestions(batchId, code)
          if (cancelled) return
          setUserSuggestions(prev => ({ ...prev, [code]: data.suggestions }))
          if (data.suggestions && data.suggestions.length > 0) {
            const first = data.suggestions[0]
            if (first && first.confidence >= 0.7) {
              setSelectedUser(prev => ({ ...prev, [code]: first.id }))
            }
          }
        } catch (err) {
          console.error('Failed to load suggestions for', code, err)
        } finally {
          if (!cancelled) {
            setLoadingSuggestions(prev => ({ ...prev, [code]: false }))
          }
        }
      }
    }
    
    loadAll()
    return () => { cancelled = true }
  }, [batchId, pendingTeachers])

  const handleShowPdfPreview = useCallback((sessionIndex: number) => {
    setActivePdfSessionIndex(sessionIndex)
    setPdfViewerOpen(true)
  }, [])

  const handleAssign = async (teacherCode: string) => {
    if (!pendingTeachersRef.current.includes(teacherCode)) {
      console.warn('[TeacherAssignmentDialog] teacherCode not in current pendingTeachers, but proceeding anyway:', teacherCode, 'pendingTeachers:', pendingTeachersRef.current, 'batchId:', batchId)
    }
    setAssigning(teacherCode)
    setError(null)
    try {
      const userId = createUserMode[teacherCode] ? undefined : selectedUser[teacherCode] ?? undefined
      const email = createUserMode[teacherCode] ? newUserEmail[teacherCode] : undefined
      const result = await timetableImportV2Api.assignTeacher(batchId, teacherCode, userId, email)
      
      // Clear suggestions for this teacher and mark all remaining teachers as needing reload
      // (because the available users change after each assignment)
      setUserSuggestions(prev => {
        const next = { ...prev }
        delete next[teacherCode]
        return next
      })
      setSelectedUser(prev => {
        const next = { ...prev }
        delete next[teacherCode]
        return next
      })
      
      onAssigned(teacherCode)
      if (result.remaining_pending === 0) {
        onComplete()
      }
    } catch (err: any) {
      console.error('Assign failed:', err)
      setError(err.response?.data?.detail || 'Zuordnung fehlgeschlagen')
    } finally {
      setAssigning(null)
    }
  }

  const handleSkip = async (teacherCode: string) => {
    if (!pendingTeachersRef.current.includes(teacherCode)) {
      console.warn('[TeacherAssignmentDialog] teacherCode not in current pendingTeachers, but proceeding anyway:', teacherCode, 'pendingTeachers:', pendingTeachersRef.current, 'batchId:', batchId)
    }
    setAssigning(teacherCode)
    setError(null)
    try {
      const result = await timetableImportV2Api.skipTeacher(batchId, teacherCode)
      setUserSuggestions({})
      onSkipped(teacherCode)
      if (result.remaining_pending === 0) {
        onComplete()
      }
    } catch (err: any) {
      console.error('Skip failed:', err)
      setError(err.response?.data?.detail || 'Überspringen fehlgeschlagen')
    } finally {
      setAssigning(null)
    }
  }

  const handleSkipAll = async () => {
    if (pendingTeachersRef.current.length === 0) {
      console.warn('[TeacherAssignmentDialog] pendingTeachers is empty, batchId:', batchId)
      onComplete()
      return
    }
    setAssigning('all')
    setError(null)
    try {
      // Skip all remaining pending teachers at once
      // The backend will only skip teachers that are still in pending_teachers
      // (already matched teachers have been removed from the list)
      await timetableImportV2Api.skipAllPending(batchId)
      setUserSuggestions({})
      pendingTeachersRef.current.forEach(code => onSkipped(code))
      // Use onSkipAllComplete if available to avoid re-opening dialog
      if (onSkipAllComplete) {
        onSkipAllComplete()
      } else {
        onComplete()
      }
    } catch (err: any) {
      console.error('Skip all failed:', err)
      setError(err.response?.data?.detail || 'Alle überspringen fehlgeschlagen')
    } finally {
      setAssigning(null)
    }
  }

  const filteredUsers = useMemo(() => {
    const users = userSuggestions[selectedTeacher] || []
    if (!userSearchQuery.trim()) return users
    const query = userSearchQuery.toLowerCase()
    return users.filter(u =>
      (u.first_name?.toLowerCase() || '').includes(query) ||
      (u.last_name?.toLowerCase() || '').includes(query) ||
      u.email.toLowerCase().includes(query) ||
      (u.teacher_code?.toLowerCase() || '').includes(query)
    )
  }, [userSuggestions, selectedTeacher, userSearchQuery])

  const highConfidenceUsers = filteredUsers.filter(u => u.confidence >= 0.7)
  const otherUsers = filteredUsers.filter(u => u.confidence < 0.7)

  const badgeLabel = (user: UserSuggestion) => {
    const code = user.teacher_code?.trim()
    if (code) return code
    const first = user.first_name?.charAt(0) || ''
    const last = user.last_name?.charAt(0) || ''
    return (first + last).toUpperCase() || user.email.charAt(0).toUpperCase()
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
          <div className="flex flex-1 overflow-hidden">
            {/* Left Side: Teacher List */}
            <div className="w-80 border-r border-gray-200 bg-gray-50 overflow-y-auto flex flex-col">
              <div className="p-3 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ausstehend</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                    {pendingTeachers.length}
                  </span>
                </div>
              </div>

              <div className="p-3 flex-1 space-y-2">
                {pendingTeachers.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-4">
                    Keine ausstehenden Lehrer
                  </div>
                )}
                {pendingTeachers.map((code) => {
                  const isSelected = code === selectedTeacher
                  const count = perTeacherCounts[code] || 0
                  const teacherSessions = getSessionsForTeacher(code)
                  // Show "1 PDF" if there's at least one session, regardless of actual count
                  // This is because all sessions for unknown teachers are grouped together
                  const pdfCount = teacherSessions.length > 0 ? 1 : 0

                  return (
                    <div
                      key={code}
                      onClick={() => setSelectedTeacher(code)}
                      className={`bg-white rounded-lg p-3 shadow-sm border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-l-4 border-l-blue-500 border-gray-100'
                          : 'border-l-4 border-l-gray-300 border-gray-100 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 text-sm truncate">{formatTeacherCode(code)}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">{count} Einträge</span>
                            {pdfCount > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedTeacher(code)
                                  handleShowPdfPreview(0)
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-0.5 bg-blue-50 px-1.5 py-0.5 rounded"
                              >
                                <Eye className="w-3 h-3" />
                                {pdfCount} PDFs
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Right Side: Assignment Panel */}
            <div className="flex-1 overflow-y-auto flex flex-col">
              <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="flex-1 px-6 py-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                    {error}
                  </div>
                )}

                {loadingSuggestions[selectedTeacher] ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Lade Benutzer-Vorschläge...
                  </div>
                ) : (
                  <>
                    <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setCreateUserMode(prev => ({ ...prev, [selectedTeacher]: false }))}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                          !createUserMode[selectedTeacher]
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200/50'
                        }`}
                      >
                        Bestehenden Benutzer
                      </button>
                      <button
                        onClick={() => setCreateUserMode(prev => ({ ...prev, [selectedTeacher]: true }))}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                          createUserMode[selectedTeacher]
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200/50'
                        }`}
                      >
                        Neuen Benutzer erstellen
                      </button>
                    </div>

                    {!createUserMode[selectedTeacher] ? (
                      <>
                        <div className="mb-3">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Benutzer suchen..."
                              value={userSearchQuery}
                              onChange={(e) => setUserSearchQuery(e.target.value)}
                              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            {userSearchQuery && (
                              <button
                                onClick={() => setUserSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                          {highConfidenceUsers.length > 0 && (
                            <>
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-1">
                                Vorschläge
                              </div>
                              {highConfidenceUsers.map((user) => (
                                <button
                                  key={user.id}
                                  onClick={() => setSelectedUser(prev => ({ ...prev, [selectedTeacher]: user.id }))}
                                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                                    selectedUser[selectedTeacher] === user.id
                                      ? 'bg-blue-50 border-blue-500'
                                      : 'hover:bg-blue-50 border-transparent hover:border-blue-200'
                                  }`}
                                >
                                  <div className="min-w-10 h-10 px-1.5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xs font-mono">
                                    {badgeLabel(user)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {user.first_name} {user.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border-2 ${
                                    selectedUser[selectedTeacher] === user.id
                                      ? 'border-blue-600 bg-blue-600'
                                      : 'border-gray-300'
                                  }`}>
                                    {selectedUser[selectedTeacher] === user.id && (
                                      <CheckCircle2 className="w-3 h-3 text-white m-0.5" />
                                    )}
                                  </div>
                                </button>
                              ))}
                            </>
                          )}

                          {otherUsers.length > 0 && (
                            <>
                              {otherUsers.map((user) => (
                                <button
                                  key={user.id}
                                  onClick={() => setSelectedUser(prev => ({ ...prev, [selectedTeacher]: user.id }))}
                                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                                    selectedUser[selectedTeacher] === user.id
                                      ? 'bg-blue-50 border-blue-500'
                                      : 'hover:bg-gray-50 border-transparent'
                                  }`}
                                >
                                  <div className="min-w-10 h-10 px-1.5 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-semibold text-xs font-mono">
                                    {badgeLabel(user)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {user.first_name} {user.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border-2 ${
                                    selectedUser[selectedTeacher] === user.id
                                      ? 'border-blue-600 bg-blue-600'
                                      : 'border-gray-300'
                                  }`}>
                                    {selectedUser[selectedTeacher] === user.id && (
                                      <CheckCircle2 className="w-3 h-3 text-white m-0.5" />
                                    )}
                                  </div>
                                </button>
                              ))}
                            </>
                          )}

                          {filteredUsers.length === 0 && (
                            <div className="text-center py-4 text-gray-500 text-sm">
                              Keine Benutzer gefunden
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            E-Mail-Adresse
                          </label>
                          <input
                            type="email"
                            placeholder="name@schule.ch"
                            value={newUserEmail[selectedTeacher] || ''}
                            onChange={(e) => setNewUserEmail(prev => ({ ...prev, [selectedTeacher]: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSkipAll}
                disabled={assigning === 'all'}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 disabled:opacity-50"
              >
                <SkipForward className="w-4 h-4" />
                Alle überspringen
              </button>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleSkip(selectedTeacher)}
              disabled={assigning === selectedTeacher}
            >
              Überspringen
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleAssign(selectedTeacher)}
              disabled={
                assigning === selectedTeacher ||
                (!createUserMode[selectedTeacher] && !selectedUser[selectedTeacher]) ||
                (createUserMode[selectedTeacher] && !newUserEmail[selectedTeacher])
              }
            >
              {assigning === selectedTeacher ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Zuordnen...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Zuordnen & Fortfahren
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <PdfTeacherPreviewModal
        isOpen={pdfViewerOpen}
        teacherCode={selectedTeacher}
        sessions={sessions}
        initialSessionIndex={activePdfSessionIndex}
        onClose={() => setPdfViewerOpen(false)}
      />
    </>
  )
}
